/**
 * Dependencies.
 */
import { debounce, orderBy, uniqBy } from 'lodash-es';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

import PackageModel from '@/api/package/package.model';
import { servicePointOrderPackageService } from '@/api/service-point/order-package/order-package.service';
import { ServicePointOrderModel } from '@/api/service-point/order/order.model';
import { servicePointOrderService } from '@/api/service-point/order/order.service';
import OrderSteps from '@/modules/service-point/components/order/order-steps/order-steps.component.vue';
import orderState from '@/modules/service-point/constants/order.constant';
import { servicePointStore } from '@/modules/service-point/store';

/**
 * Component.
 */
@Component({
  components: { 'order-steps': OrderSteps },
  name: 'collect-order-scan-scene'
})
export default class extends Vue {
  /**
   * Property `orderState`.
   */
  orderState = orderState;

  /**
   * Property `isLoadingOrder`.
   */
  isLoadingOrder = false;

  /**
   * Property `isLoadingPackages`.
   */
  isLoadingPackages = false;

  /**
   * Property `hasValidTrackingNumberKey`.
   */
  hasValidTrackingNumberKey = false;

  /**
   * Property `pickUpPin`.
   */
  pickUpPin: string = null;

  /**
   * Property `trackingNumberKey`.
   */
  trackingNumberKey: string = null;

  /**
   * Property `order`.
   */
  order: ServicePointOrderModel = null;

  /**
   * Property `orderPackages`.
   */
  orderPackages: PackageModel[] = [];

  /**
   * Property `searchOrder`.
   */
  searchOrder = debounce(this.handleGetOrderPackages, 250, {
    leading: true,
    trailing: false
  });

  /**
   * Property `searchPackageCode`.
   */
  searchPackageCode = debounce(this.handleValidateCode, 250, {
    leading: true,
    trailing: false
  });

  /**
   * Getter `packages`.
   */
  get packages() {
    let packages = this.orderPackages;

    packages = uniqBy(packages, 'id');
    packages = orderBy(packages, ['status.code'], ['desc']);

    return packages;
  }

  /**
   * Getter `hasOrder`.
   */
  get hasOrder() {
    return this.order !== null;
  }

  /**
   * Getter `totalSteps`.
   */
  get totalSteps() {
    return 3;
  }

  /**
   * Getter `step`.
   */
  get step() {
    return this.packages.length ? 2 : 1;
  }

  /**
   * Getter `notifySucessSettings`.
   */
  get notifySucessSettings() {
    return {
      backgroundColor: 'var(--pk-color-success-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-success)',
      iconText: 'check_circle',
      id: 'deliver-success',
      progressBarColor: 'var(--pk-color-success)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Getter `notifyErrorSettings`.
   */
  get notifyErrorSettings() {
    return {
      backgroundColor: 'var(--pk-color-error-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-error)',
      iconText: 'warning',
      id: 'deviver-error',
      progressBarColor: 'var(--pk-color-error)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Getter `trackingNumber`.
   */
  get trackingNumber() {
    const { order } = this || {};
    const { delivery } = order || {};
    const { trackingNumber = null } = delivery || {};

    return trackingNumber;
  }

  /**
   * Getter `currentTrackingNumberKey`.
   */
  get currentTrackingNumberKey() {
    const trackingNumberKey = servicePointStore.deliverTrackingNumberKey;

    return trackingNumberKey;
  }

  /**
   * Lifecycle mounted hook.
   */
  mounted() {
    const { currentTrackingNumberKey } = this || {};

    if (currentTrackingNumberKey) {
      this.trackingNumberKey = currentTrackingNumberKey;

      this.searchOrder(this.trackingNumberKey, 'key');
    } else {
      servicePointStore.setDeliverTrackingNumberKey(null);
      servicePointStore.setOrder(null);
    }
  }

  /**
   * Handle get order packages.
   */
  async handleGetOrderPackages(pickUpCode: string, type: 'key' | 'pin') {
    if (!!pickUpCode && !this.isLoadingOrder) {
      this.isLoadingOrder = true;

      try {
        let order = null;

        if (type === 'key') {
          order = await servicePointOrderService.getByKey(pickUpCode);
        } else {
          order = await servicePointOrderService.getByPin(pickUpCode);
        }

        if (
          order?.status?.code === this.orderState.RECEIVE_PENDING_TO_COLLECT
        ) {
          this.order = { ...order };

          const { trackingNumberKey = null, packages = [] } = this.order || {};

          this.trackingNumberKey =
            type === 'key' ? pickUpCode : trackingNumberKey;

          this.orderPackages = packages;
          this.hasValidTrackingNumberKey = true;

          if (!this.currentTrackingNumberKey) {
            this.$kit.notify.show(
              this.i18n('SERVICE_POINT.LABELS.SUCCESS'),
              this.i18n(
                'SERVICE_POINT.SUCCESS.CODE_READ',
                false,
                'upperFirst',
                {
                  code: pickUpCode
                }
              ),
              this.notifySucessSettings
            );
          }
        } else {
          throw new Error('Collect with invalid state');
        }
      } catch (error) {
        const { raw } = error || {};
        const { code } = raw || {};

        if (!this.hasValidTrackingNumberKey) {
          switch (code) {
            case 3413:
              this.$kit.notify.show(
                this.i18n('SERVICE_POINT.LABELS.ERROR'),
                this.i18n('SERVICE_POINT.ERRORS.ORDER_DELIVERED'),
                this.notifyErrorSettings
              );
              break;

            default:
              this.$kit.notify.show(
                this.i18n('SERVICE_POINT.LABELS.ERROR'),
                this.i18n(
                  'SERVICE_POINT.ERRORS.VALIDATING_CODE',
                  false,
                  'upperFirst',
                  {
                    code: pickUpCode
                  }
                ),
                this.notifyErrorSettings
              );
              break;
          }
        }

        this.pickUpPin = null;

        if (process.env.BUILD_MODE !== 'prd') {
          console.error({
            error,
            method: 'handleGetOrderPackages',
            scene: 'CollectOrderScanScene'
          });
        }
      } finally {
        this.isLoadingOrder = false;
      }
    }
  }

  /**
   * Handle validate code.
   */
  async handleValidateCode(code: string) {
    const packageCode = code;

    if (!!packageCode && !this.isLoadingPackages) {
      try {
        this.isLoadingPackages = true;

        await servicePointOrderPackageService.receive(packageCode);

        this.$kit.notify.show(
          this.i18n('SERVICE_POINT.LABELS.SUCCESS'),
          this.i18n('SERVICE_POINT.SUCCESS.CODE_READ', false, 'upperFirst', {
            code
          }),
          this.notifySucessSettings
        );
      } catch (error) {
        this.$kit.notify.show(
          this.i18n('SERVICE_POINT.LABELS.ERROR'),
          this.i18n('SERVICE_POINT.ERRORS.VALIDATING_CODE'),
          this.notifyErrorSettings
        );

        if (process.env.BUILD_MODE !== 'prd') {
          console.error({
            error,
            method: 'handleValidateCode',
            scene: 'CollectOrderScanScene'
          });
        }
      } finally {
        this.isLoadingPackages = false;
      }
    }
  }

  /**
   * Handle go to confirm deliver.
   */
  handleGoToConfirmDeliver() {
    servicePointStore.setDeliverTrackingNumberKey(this.trackingNumberKey);
    servicePointStore.setOrder(this.order);

    this.$router.push({
      name: 'ServicePointCollectOrderConfirm',
      params: {
        totalSteps: String(this.totalSteps)
      }
    });
  }

  /**
   * Handle cancel deliver.
   */
  handleCancelDeliver() {
    servicePointStore.setDeliverTrackingNumberKey(null);
    servicePointStore.setOrder(null);

    this.$router.push({
      name: 'ServicePointHome'
    });
  }

  /**
   * Handle collect without pin.
   */
  handleCollectWithoutPin() {
    this.$router.push({
      name: 'ServicePointCollectOrderWithoutPinList'
    });
  }
}
